import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AppointmentsCard from './components/AppointmentsCard';
import ParentNotificationsCard from './components/NotificationsCard';
import ProfesoriPanel from '../class-management-hub/components/ProfesoriPanel';
import ParentSecretariatPanel from './components/ParentSecretariatPanel';
import HomeworkQuizModal from '../student-dashboard/components/HomeworkQuizModal';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import SubscriptionRequiredModal from '../../components/ui/SubscriptionRequiredModal';
import { isSubscriptionError, type SubscriptionFeature } from '../../lib/subscriptionGate';
import type { NotificationItem } from '../../lib/teacherApi';

type Copil = {
  id: number;
  full_name: string;
  classroom_id: number | null;
  classroom_name: string | null;
  age: number | string;
  over16?: boolean;
  username?: string | null;
  activated?: boolean;
  has_account: boolean;
  avatar_url?: string | null;
};

type GradebookConfig = {
  id: number;
  classroom_id: number;
  school_year: string;
  structure: 'semestru' | 'trimestru' | 'module' | string;
  education_level: string;
  locked: boolean;
};

type OrarEntry = {
  id: number;
  timetable_id: number;
  day_of_week: number; // 0..4
  start_time: string;
  end_time: string;
  subject: string;
  room?: string | null;
  lesson_type?: string | null;
  comment?: string | null;
  teacher_id: number;
  teacher_name?: string | null;
};

type HomeworkFile = { id: number; file_url: string; original_name: string };
type HomeworkSubmission = {
  id: number;
  comment?: string | null;
  created_at: string;
  updated_at: string;
  files: HomeworkFile[];
};
type HomeworkItem = {
  id: number;
  teacher_id: number;
  teacher_name?: string | null;
  subject?: string | null;
  title: string;
  description?: string | null;
  deadline?: string | null;
  created_at?: string | null;
  has_quiz?: boolean;
  quiz_time_limit_seconds?: number | null;
  quiz_submission?: {
    submitted_at?: string | null;
    score?: number | null;
    total?: number | null;
    duration_seconds?: number | null;
  } | null;
  files: HomeworkFile[];
  submission: HomeworkSubmission | null;
};

type GradeItem = {
  id: number;
  subject_id?: number | null;
  subject_name?: string | null;
  teacher_name?: string | null;
  value?: number | null;
  qualifier?: string | null;
  comment?: string | null;
  is_thesis: boolean;
  is_homework: boolean;
  is_lab: boolean;
  is_seminar: boolean;
  is_final: boolean;
  is_conduct: boolean;
  created_at: string;
};

type AbsenceItem = {
  id: number;
  subject_id?: number | null;
  subject_name?: string | null;
  date?: string | null;
  justified?: string | null;
  justification_reason?: string | null;
  created_at: string;
};

type SectionKey = 'orar' | 'teme_profesor' | 'teme_elev' | 'note' | 'absente' | 'profesori' | 'secretariat';

const days = ['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri'];

function normalizeStructure(s: string): 'semestru' | 'trimestru' | 'module' | null {
  const v = (s || '').toString().trim().toLowerCase();
  if (!v) return null;
  if (v.startsWith('sem')) return 'semestru';
  if (v.startsWith('tri')) return 'trimestru';
  if (v.startsWith('mod')) return 'module';
  if (['semestru', 'trimestru', 'module'].includes(v)) return v as any;
  return null;
}

function periodLabels(structure: 'semestru' | 'trimestru' | 'module' | null) {
  if (structure === 'semestru') return ['Semestrul 1', 'Semestrul 2'];
  if (structure === 'trimestru') return ['Trimestrul 1', 'Trimestrul 2', 'Trimestrul 3'];
  if (structure === 'module') return ['Modul 1', 'Modul 2', 'Modul 3', 'Modul 4', 'Modul 5'];
  return ['Perioada 1'];
}

function gradeToNumber(g: GradeItem): number | null {
  if (g.is_conduct) return null;
  if (typeof g.value === 'number' && Number.isFinite(g.value)) return g.value;

  const q = (g.qualifier || '').toString().trim().toUpperCase();
  // Mapare uzuala primar: FB/B/S/I
  if (q === 'FB') return 10;
  if (q === 'B') return 8;
  if (q === 'S') return 6;
  if (q === 'I') return 4;
  return null;
}

function computeAverage(grades: GradeItem[]): { avg: number | null; count: number } {
  const nums = grades.map(gradeToNumber).filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  if (nums.length === 0) return { avg: null, count: 0 };
  const sum = nums.reduce((a, b) => a + b, 0);
  return { avg: sum / nums.length, count: nums.length };
}

function gradeTypeLabel(g: GradeItem): string {
  if (g.is_conduct) return 'Purtare';
  if (g.is_thesis) return 'Teza';
  if (g.is_final) return 'Finala';
  if (g.is_homework) return 'Tema';
  if (g.is_lab) return 'Laborator';
  if (g.is_seminar) return 'Seminar';
  return 'Nota';
}

function ratingLabelFromAvg(avg: number): string {
  if (avg >= 9.5) return 'FB';
  if (avg >= 7.5) return 'B';
  if (avg >= 5.5) return 'S';
  return 'I';
}

 const ParentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { me, uiRole, refresh, signOut } = useAuth();
  const apiBase = useMemo(() => (api.defaults.baseURL || 'http://localhost:8000').toString().replace(/\/$/, ''), []);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<SubscriptionFeature>('materials_download');

  const openUpgrade = (feature: SubscriptionFeature) => {
    setUpgradeFeature(feature);
    setUpgradeOpen(true);
  };

  const [parentPlan, setParentPlan] = useState<string>('free');
  const [parentUnrestricted, setParentUnrestricted] = useState<boolean>(false);

  const doDownloadHomeworkFile = async (fileId: number) => {
    try {
      const resp = await api.get(`/api/teme/fisiere/${encodeURIComponent(String(fileId))}/download`);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
      } catch (e: any) {
        if (isSubscriptionError(e)) {
          openUpgrade('materials_download');
          return;
        }
        alert(e?.response?.data?.detail || 'Nu am putut descărca fișierul.');
      }
    };

  const doDownloadSubmissionFile = async (fileId: number) => {
    try {
      const resp = await api.get(`/api/teme/rezolvari/fisiere/${encodeURIComponent(String(fileId))}/download`);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
      } catch (e: any) {
        if (isSubscriptionError(e)) {
          openUpgrade('materials_download');
          return;
        }
        alert(e?.response?.data?.detail || 'Nu am putut descărca fișierul.');
      }
    };

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);

  const [copii, setCopii] = useState<Copil[]>([]);
  const [loadingCopii, setLoadingCopii] = useState(true);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const selectedChild = useMemo(
    () => copii.find((c) => Number(c.id) === Number(selectedChildId)) || null,
    [copii, selectedChildId]
  );

  const [activeSection, setActiveSection] = useState<SectionKey>('orar');

  const [gradebookConfigs, setGradebookConfigs] = useState<GradebookConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [activeGradebookId, setActiveGradebookId] = useState<number | null>(null);
  const [activePeriodIndex, setActivePeriodIndex] = useState(0);

  const activeGradebook = useMemo(
    () => gradebookConfigs.find((c) => Number(c.id) === Number(activeGradebookId)) || null,
    [gradebookConfigs, activeGradebookId]
  );
  const activeStructure = normalizeStructure(activeGradebook?.structure || '');
  const periodTabs = periodLabels(activeStructure);

  const [orar, setOrar] = useState<OrarEntry[]>([]);
  const [loadingOrar, setLoadingOrar] = useState(false);
  const [orarError, setOrarError] = useState<string | null>(null);

  const [teme, setTeme] = useState<HomeworkItem[]>([]);
  const [quizHomeworkId, setQuizHomeworkId] = useState<number | null>(null);
  const [loadingTeme, setLoadingTeme] = useState(false);
  const [temeError, setTemeError] = useState<string | null>(null);

  const [note, setNote] = useState<GradeItem[]>([]);
  const [loadingNote, setLoadingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  const [absente, setAbsente] = useState<AbsenceItem[]>([]);
  const [loadingAbsente, setLoadingAbsente] = useState(false);
  const [absenteError, setAbsenteError] = useState<string | null>(null);

  const [showPwdModal, setShowPwdModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  const [activateOpen, setActivateOpen] = useState(false);
  const [activateUsername, setActivateUsername] = useState('');
  const [activatePassword, setActivatePassword] = useState('');
  const [activateConfirm, setActivateConfirm] = useState('');
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activateLoading, setActivateLoading] = useState(false);

  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingAvg, setRatingAvg] = useState<number | null>(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [ratingDelta, setRatingDelta] = useState<number | null>(null);
  const [ratingDeltaPct, setRatingDeltaPct] = useState<number | null>(null);
  const [ratingDirection, setRatingDirection] = useState<'up' | 'down' | 'flat' | null>(null);

  useEffect(() => {
    if (uiRole === 'parent' && me?.must_change_password) setShowPwdModal(true);
  }, [me?.must_change_password, uiRole]);

  useEffect(() => {
    const loadChildren = async () => {
      try {
        setLoadingCopii(true);
        const resp = await api.get('/api/parinte/copii');
        setCopii(Array.isArray(resp.data) ? (resp.data as Copil[]) : []);
      } catch {
        setCopii([]);
      } finally {
        setLoadingCopii(false);
      }
    };
    void loadChildren();
  }, []);

  useEffect(() => {
    if (uiRole !== 'parent') return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/api/subscriptions/me');
        const plan = String((resp.data as any)?.plan || 'free').toLowerCase();
        const unrestricted = Boolean((resp.data as any)?.unrestricted);
        if (cancelled) return;
        setParentPlan(plan);
        setParentUnrestricted(unrestricted);
      } catch {
        if (!cancelled) {
          setParentPlan('free');
          setParentUnrestricted(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uiRole]);

  const reloadChildren = async () => {
    try {
      setLoadingCopii(true);
      const resp = await api.get('/api/parinte/copii');
      setCopii(Array.isArray(resp.data) ? (resp.data as Copil[]) : []);
    } finally {
      setLoadingCopii(false);
    }
  };

  const openActivateModal = () => {
    if (!selectedChild) return;

    const isOver16 =
      typeof selectedChild.over16 === 'boolean'
        ? selectedChild.over16
        : typeof selectedChild.age === 'number' && selectedChild.age >= 16;

    if (isOver16) {
      window.alert('Copilul are peste 16 ani; contul se activează separat (email).');
      return;
    }

    const activatedUnder16Count = copii.filter((c) => {
      const cOver16 =
        typeof c.over16 === 'boolean' ? c.over16 : typeof c.age === 'number' && c.age >= 16;
      return !cOver16 && Boolean(c.activated);
    }).length;

    const isFree = !parentUnrestricted && String(parentPlan || 'free').toLowerCase() === 'free';
    const childAlreadyActivated = Boolean(selectedChild.activated);
    if (isFree && !childAlreadyActivated && activatedUnder16Count >= 1) {
      openUpgrade('child_activation');
      return;
    }

    setActivateError(null);
    setActivateUsername(String(selectedChild?.username || '').trim());
    setActivatePassword('');
    setActivateConfirm('');
    setActivateOpen(true);
  };

  const openAvatarModal = () => {
    setAvatarError(null);
    setAvatarFile(null);
    setAvatarOpen(true);
  };

  const uploadChildAvatar = async () => {
    if (!selectedChildId) return;
    setAvatarError(null);
    if (!avatarFile) return setAvatarError('Selecteaza o imagine.');
    setAvatarLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', avatarFile);
      await api.post(`/api/parinte/copii/${selectedChildId}/avatar`, fd);
      setAvatarOpen(false);
      await reloadChildren();
    } catch (err: any) {
      setAvatarError(err?.response?.data?.detail || 'Nu s-a putut salva imaginea.');
    } finally {
      setAvatarLoading(false);
    }
  };

  const deleteChildAvatar = async () => {
    if (!selectedChildId) return;
    setAvatarError(null);
    if (!window.confirm('Stergi imaginea de profil a copilului?')) return;
    setAvatarLoading(true);
    try {
      await api.delete(`/api/parinte/copii/${selectedChildId}/avatar`);
      setAvatarOpen(false);
      await reloadChildren();
    } catch (err: any) {
      setAvatarError(err?.response?.data?.detail || 'Nu s-a putut sterge imaginea.');
    } finally {
      setAvatarLoading(false);
    }
  };

  const activateChildAccount = async () => {
    if (!selectedChildId) return;
    setActivateError(null);
    if (!activateUsername.trim()) return setActivateError('Completeaza username.');
    if (!activatePassword) return setActivateError('Completeaza parola.');
    if (activatePassword !== activateConfirm) return setActivateError('Parolele nu coincid.');

    setActivateLoading(true);
    try {
      await api.put(`/api/parinte/copii/${selectedChildId}/activate`, {
        username: activateUsername.trim(),
        new_password: activatePassword,
        confirm_password: activateConfirm,
      });
      setActivateOpen(false);
      await reloadChildren();
      alert('Contul elevului a fost activat. Elevul se poate autentifica cu username + parola.');
    } catch (err: any) {
      if (isSubscriptionError(err)) {
        setActivateOpen(false);
        openUpgrade('child_activation');
        return;
      }
      setActivateError(err?.response?.data?.detail || 'Nu s-a putut activa contul elevului.');
    } finally {
      setActivateLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedChildId && copii.length) setSelectedChildId(copii[0].id);
  }, [copii, selectedChildId]);

  useEffect(() => {
    const rawChildId = (searchParams.get('childId') || '').trim();
    const childId = rawChildId ? Number(rawChildId) : NaN;
    if (Number.isFinite(childId) && copii.some((c) => Number(c.id) === Number(childId))) {
      setSelectedChildId(Number(childId));
    }

    const rawSection = (searchParams.get('section') || '').trim() as SectionKey;
    const allowed: SectionKey[] = ['orar', 'teme_profesor', 'teme_elev', 'note', 'absente', 'profesori', 'secretariat'];
    if (allowed.includes(rawSection)) {
      setActiveSection(rawSection);
    }
  }, [copii, searchParams]);

  useEffect(() => {
    const loadConfigs = async (classroomId: number) => {
      setLoadingConfigs(true);
      try {
        const resp = await api.get(`/api/catalog/config/all/${classroomId}`);
        const cfgs = Array.isArray(resp.data) ? (resp.data as GradebookConfig[]) : [];
        setGradebookConfigs(cfgs);
        setActiveGradebookId(cfgs[0]?.id ?? null);
        setActivePeriodIndex(0);
      } catch {
        setGradebookConfigs([]);
        setActiveGradebookId(null);
        setActivePeriodIndex(0);
      } finally {
        setLoadingConfigs(false);
      }
    };

    if (!selectedChild?.classroom_id) {
      setGradebookConfigs([]);
      setActiveGradebookId(null);
      setActivePeriodIndex(0);
      return;
    }
    void loadConfigs(selectedChild.classroom_id);
  }, [selectedChild?.classroom_id]);

  useEffect(() => {
    const loadOrar = async () => {
      if (!selectedChildId) return;
      if (activeSection !== 'orar') return;
      setLoadingOrar(true);
      setOrarError(null);
      try {
        const resp = await api.get(`/api/parinte/copii/${selectedChildId}/orar`, {
          params: { gradebook_config_id: activeGradebookId ?? undefined, period_index: activePeriodIndex },
        });
        setOrar(Array.isArray(resp.data?.entries) ? (resp.data.entries as OrarEntry[]) : []);
      } catch {
        setOrar([]);
        setOrarError('Eroare la incarcarea orarului.');
      } finally {
        setLoadingOrar(false);
      }
    };
    void loadOrar();
  }, [activeGradebookId, activePeriodIndex, activeSection, selectedChildId]);

  useEffect(() => {
    const loadTeme = async () => {
      if (!selectedChildId) return;
      if (!activeGradebookId) return;
      if (activeSection !== 'teme_profesor' && activeSection !== 'teme_elev') return;
      setLoadingTeme(true);
      setTemeError(null);
      try {
        const resp = await api.get(`/api/parinte/copii/${selectedChildId}/teme`, {
          params: { gradebook_config_id: activeGradebookId, period_index: activePeriodIndex },
        });
        setTeme(Array.isArray(resp.data) ? (resp.data as HomeworkItem[]) : []);
      } catch {
        setTeme([]);
        setTemeError('Eroare la incarcarea temelor.');
      } finally {
        setLoadingTeme(false);
      }
    };
    void loadTeme();
  }, [activeGradebookId, activePeriodIndex, activeSection, selectedChildId]);

  useEffect(() => {
    const loadNote = async () => {
      if (!selectedChildId) return;
      if (activeSection !== 'note') return;
      setLoadingNote(true);
      setNoteError(null);
      try {
        const resp = await api.get(`/api/grades/student/${selectedChildId}`, {
          params: { gradebook_config_id: activeGradebookId ?? undefined, period_index: activePeriodIndex },
        });
        setNote(Array.isArray(resp.data) ? (resp.data as GradeItem[]) : []);
      } catch {
        setNote([]);
        setNoteError('Eroare la incarcarea notelor.');
      } finally {
        setLoadingNote(false);
      }
    };
    void loadNote();
  }, [activeGradebookId, activePeriodIndex, activeSection, selectedChildId]);

  useEffect(() => {
    const loadRating = async () => {
      if (!selectedChildId) {
        setRatingAvg(null);
        setRatingCount(0);
        setRatingDelta(null);
        setRatingDirection(null);
        return;
      }
      if (!activeGradebookId) {
        setRatingAvg(null);
        setRatingCount(0);
        setRatingDelta(null);
        setRatingDirection(null);
        return;
      }

      setRatingLoading(true);
      try {
        const currentParams = { gradebook_config_id: activeGradebookId, period_index: activePeriodIndex };
        const prevIdx = activePeriodIndex - 1;
        const prevParams =
          prevIdx >= 0 ? { gradebook_config_id: activeGradebookId, period_index: prevIdx } : null;

        const [curResp, prevResp] = await Promise.all([
          api.get(`/api/grades/student/${selectedChildId}`, { params: currentParams }),
          prevParams ? api.get(`/api/grades/student/${selectedChildId}`, { params: prevParams }) : Promise.resolve({ data: [] }),
        ]);

        const curGrades = Array.isArray(curResp.data) ? (curResp.data as GradeItem[]) : [];
        const prevGrades = Array.isArray((prevResp as any)?.data) ? ((prevResp as any).data as GradeItem[]) : [];

        const cur = computeAverage(curGrades);
        const prev = computeAverage(prevGrades);

        setRatingAvg(cur.avg);
        setRatingCount(cur.count);

        if (cur.avg == null || prevIdx < 0 || prev.avg == null) {
          setRatingDelta(null);
          setRatingDeltaPct(null);
          setRatingDirection(null);
        } else {
          const delta = cur.avg - prev.avg;
          setRatingDelta(delta);
          const pct = prev.avg !== 0 ? (delta / prev.avg) * 100 : null;
          setRatingDeltaPct(pct);
          const abs = Math.abs(delta);
          if (abs < 0.25) setRatingDirection('flat');
          else setRatingDirection(delta > 0 ? 'up' : 'down');
        }
      } catch {
        setRatingAvg(null);
        setRatingCount(0);
        setRatingDelta(null);
        setRatingDeltaPct(null);
        setRatingDirection(null);
      } finally {
        setRatingLoading(false);
      }
    };

    void loadRating();
  }, [activeGradebookId, activePeriodIndex, selectedChildId]);

  useEffect(() => {
    const loadAbsente = async () => {
      if (!selectedChildId) return;
      if (activeSection !== 'absente') return;
      setLoadingAbsente(true);
      setAbsenteError(null);
      try {
        const resp = await api.get(`/api/absente/student/${selectedChildId}`, {
          params: { gradebook_config_id: activeGradebookId ?? undefined, period_index: activePeriodIndex },
        });
        setAbsente(Array.isArray(resp.data) ? (resp.data as AbsenceItem[]) : []);
      } catch {
        setAbsente([]);
        setAbsenteError('Eroare la incarcarea absentelor.');
      } finally {
        setLoadingAbsente(false);
      }
    };
    void loadAbsente();
  }, [activeGradebookId, activePeriodIndex, activeSection, selectedChildId]);

  const validatePassword = (pwd: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(pwd);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPwdError('Parolele nu coincid.');
      return;
    }
    if (!validatePassword(newPassword)) {
      setPwdError('Parola trebuie sa contina litere mari/mici, cifre si simboluri.');
      return;
    }
    setPwdLoading(true);
    setPwdError('');
    try {
      await api.put('/api/users/me/password/initial', {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      await refresh();
      alert('Parola a fost schimbata. Vei fi delogat.');
      signOut();
      navigate('/authentication-portal');
    } catch (err: any) {
      setPwdError(err?.response?.data?.detail || 'Eroare la schimbarea parolei.');
    } finally {
      setPwdLoading(false);
    }
  };

  const temeProfesor = teme;
  const temeElev = useMemo(() => teme.filter((t) => Boolean(t.submission)), [teme]);

  const quizResultSummary = useMemo(() => {
    if (quizHomeworkId == null) return null;
    const hw = teme.find((t) => Number(t.id) === Number(quizHomeworkId));
    const sub = hw?.quiz_submission || null;
    if (!sub) return null;
    return {
      score: sub.score ?? null,
      total: sub.total ?? null,
      durationSeconds: sub.duration_seconds ?? null,
      submittedAt: sub.submitted_at ?? null,
    };
  }, [quizHomeworkId, teme]);

  const headerUser = useMemo(
    () => ({
      role: 'parent' as const,
      name: me?.full_name || 'Parinte',
      avatar: me?.avatar_url || undefined,
    }),
    [me?.avatar_url, me?.full_name]
  );

  const ratingText = useMemo(() => {
    if (ratingLoading) return '…';
    if (ratingAvg == null) return '—';
    const avg = Math.round(ratingAvg * 10) / 10;
    return `${avg}/10`;
  }, [ratingAvg, ratingLoading]);

  const ratingSubLabel = useMemo(() => {
    if (ratingAvg == null) return null;
    return ratingLabelFromAvg(ratingAvg);
  }, [ratingAvg]);

  const groupedNote = useMemo(() => {
    const map = new Map<string, { label: string; grades: GradeItem[] }>();
    for (const g of note || []) {
      const label = g.is_conduct
        ? 'Purtare'
        : (g.subject_name || '').toString().trim() || (g.subject_id != null ? `#${g.subject_id}` : 'Fara materie');
      const key = `${g.is_conduct ? 'conduct' : 'subject'}:${label.toLowerCase()}`;
      const cur = map.get(key);
      if (cur) cur.grades.push(g);
      else map.set(key, { label, grades: [g] });
    }

    const groups = Array.from(map.values()).map((grp) => {
      const sorted = grp.grades.slice().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      const { avg, count } = computeAverage(sorted);
      const avgRounded = avg == null ? null : Math.round(avg * 100) / 100;
      return { label: grp.label, grades: sorted, avg: avgRounded, avgCount: count, totalCount: sorted.length };
    });

    return groups.sort((a, b) => a.label.localeCompare(b.label, 'ro-RO'));
  }, [note]);

  const refreshUnreadNotifications = async () => {
    try {
      const resp = await api.get<{ unread: number }>('/api/notificari/notifications/unread-count');
      const unread = Number((resp.data as any)?.unread ?? 0);
      setUnreadNotifications(Number.isFinite(unread) ? unread : 0);
    } catch {
      // ignore
    }
  };

  const loadNotifications = async () => {
    try {
      const resp = await api.get('/api/notificari/notifications', { params: { page: 1, page_size: 10 } });
      const list = Array.isArray((resp.data as any)?.items) ? ((resp.data as any).items as any[]) : [];
      setNotifications(list);
    } catch {
      setNotifications([]);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('/api/notificari/notifications/mark-as-read');
    } catch {
      // ignore
    }
    await refreshUnreadNotifications();
    await loadNotifications();
  };

  const openNotification = async (n: NotificationItem) => {
    try {
      if (!n.is_read) await api.post(`/api/notificari/notifications/${n.id}/read`);
    } catch {
      // ignore
    }
    await refreshUnreadNotifications();
    if (n.link) navigate(n.link);
  };

  useEffect(() => {
    if (!me) return;
    if (uiRole !== 'parent') return;
    void refreshUnreadNotifications();
    void loadNotifications();
    const t = window.setInterval(() => void refreshUnreadNotifications(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, uiRole]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={headerUser}
        onSignOut={() => {
          signOut();
          navigate('/authentication-portal');
        }}
      />

      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
	          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
	            <div>
	              <h1 className="text-2xl font-bold text-foreground">Dashboard parinte</h1>
	              <p className="text-sm text-muted-foreground">Orar, teme, note si absente pentru copil.</p>
	            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="AlertTriangle"
                onClick={() =>
                  navigate('/contact?category=abuse&subject=Raportare%20abuz', {
                    state: { backgroundLocation: location },
                  })
                }
                className="max-w-full"
              >
                <span className="truncate">Raportează abuz</span>
              </Button>
              <Button variant="outline" size="sm" iconName="FileText" onClick={() => navigate('/documents')}>
                Documente
              </Button>
              <button
                onClick={() => {
                  setUnreadNotifications(0);
                  navigate('/notifications');
                }}
                className="relative p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
                aria-label={`Notificări${unreadNotifications > 0 ? ` (${unreadNotifications} necitite)` : ''}`}
              >
                <Icon name="Bell" size={20} />
                {unreadNotifications > 0 && (
                  <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-error rounded-full">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-semibold text-foreground">Copii</div>
              {loadingCopii && <div className="text-sm text-muted-foreground">Se incarca...</div>}
            </div>
            {(!loadingCopii && copii.length === 0) && (
              <div className="text-sm text-muted-foreground mt-2">Nu ai copii asociati momentan.</div>
            )}
	            <div className="mt-3 flex flex-wrap gap-2">
	              {copii.map((c) => (
	                <Button
	                  key={c.id}
	                  variant={selectedChildId === c.id ? 'default' : 'outline'}
	                  size="sm"
	                  onClick={() => setSelectedChildId(c.id)}
	                  className="max-w-full"
	                >
	                  <span className="flex items-center gap-2 min-w-0">
	                    <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center overflow-hidden">
	                      {c.avatar_url ? (
	                        <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
	                      ) : (
	                        <span className="text-xs font-semibold text-foreground">{String(c.full_name || '?').trim().slice(0, 1).toUpperCase()}</span>
	                      )}
	                    </span>
	                    <span className="truncate max-w-[180px] sm:max-w-none">{c.full_name}</span>
	                  </span>
	                </Button>
	              ))}
	            </div>
	            {selectedChild && (
	              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
	                <div className="text-sm text-muted-foreground break-words">
	                  Clasa: <span className="text-foreground font-medium">{selectedChild.classroom_name || '—'}</span>
	                  {' • '}Varsta: <span className="text-foreground font-medium">{selectedChild.age}</span>
	                </div>
	
	                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
	                  {!(() => {
	                    const isOver16 = typeof selectedChild.age === 'number' && selectedChild.age >= 16;
	                    return isOver16;
	                  })() ? (
	                    <Button
	                      variant="outline"
	                      size="sm"
	                      iconName="Image"
	                      iconPosition="left"
	                      onClick={openAvatarModal}
	                      className="max-w-full"
	                    >
	                      <span className="truncate">Imagine profil</span>
	                    </Button>
	                  ) : null}
	                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    {(() => {
                      const isOver16 =
                        typeof selectedChild.over16 === 'boolean'
                          ? selectedChild.over16
                          : typeof selectedChild.age === 'number' && selectedChild.age >= 16;
                      const badgeCls =
                        isOver16 || selectedChild.activated
                          ? 'bg-success/20 text-success-foreground'
                          : 'bg-warning/20 text-warning-foreground';
                      const label = isOver16
                        ? 'Activ (email)'
                        : selectedChild.activated
                          ? `Activ (${selectedChild.username})`
                          : 'Neactiv';
                      return (
                        <>
                          <span className="text-sm text-muted-foreground">Cont elev:</span>
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded max-w-[200px] truncate ${badgeCls}`}
                            title={label}
                          >
                            {label}
                          </span>
                          {!isOver16 && (
                            <Button
                              variant="outline"
                              size="xs"
                              iconName="UserPlus"
                              iconPosition="left"
                              onClick={openActivateModal}
                              disabled={activateLoading}
                              className="max-w-full"
                            >
                              <span className="truncate">{selectedChild.activated ? 'Reseteaza' : 'Activeaza'}</span>
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </div>
	
	                  <div className="flex flex-wrap items-center gap-2 text-sm">
	                    <span className="text-muted-foreground">Rating:</span>
	                    <span className="font-semibold text-foreground">{ratingText}</span>
	                    {ratingCount > 0 && (
	                      <span className="text-xs text-muted-foreground">({ratingCount} note)</span>
	                    )}
                    {ratingSubLabel && (
                      <span className="text-xs px-2 py-0.5 rounded bg-muted text-foreground">{ratingSubLabel}</span>
                    )}
                  </div>

                  {ratingDirection && (
                    <div
                      className={`flex items-center gap-1 text-sm font-medium ${
                        ratingDirection === 'up'
                          ? 'text-success'
                          : ratingDirection === 'down'
                            ? 'text-error'
                            : 'text-muted-foreground'
                      }`}
                      title={
                        ratingDelta == null
                          ? undefined
                          : `Evolutie vs perioada anterioara: ${ratingDelta >= 0 ? '+' : ''}${(Math.round(ratingDelta * 10) / 10).toFixed(1)}${
                              ratingDeltaPct == null
                                ? ''
                                : ` (${ratingDeltaPct >= 0 ? '+' : ''}${(Math.round(ratingDeltaPct * 10) / 10).toFixed(1)}%)`
                            }`
                      }
                    >
                      <Icon
                        name={ratingDirection === 'up' ? 'ArrowUpRight' : ratingDirection === 'down' ? 'ArrowDownRight' : 'ArrowRight'}
                        size={16}
                      />
                      {ratingDelta != null && (
                        <span>
                          {`${ratingDelta >= 0 ? '+' : ''}${(Math.round(ratingDelta * 10) / 10).toFixed(1)}`}
                          {ratingDeltaPct != null
                            ? ` (${ratingDeltaPct >= 0 ? '+' : ''}${(Math.round(ratingDeltaPct * 10) / 10).toFixed(1)}%)`
                            : ''}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <AppointmentsCard />

          <div className="bg-card border border-border rounded-lg p-4 shadow-sm space-y-4">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'orar', label: 'Orar', icon: 'Calendar' },
                { key: 'teme_profesor', label: 'Teme profesor', icon: 'Upload' },
                { key: 'teme_elev', label: 'Teme elev', icon: 'FileText' },
                { key: 'note', label: 'Note', icon: 'BookOpen' },
                { key: 'absente', label: 'Absente', icon: 'UserX' },
                { key: 'profesori', label: 'Profesori', icon: 'Users' },
                { key: 'secretariat', label: 'Secretariat', icon: 'FolderOpen' },
              ].map((it) => (
                <Button
                  key={it.key}
                  variant={activeSection === it.key ? 'default' : 'outline'}
                  size="sm"
                  iconName={it.icon as any}
                  iconPosition="left"
                  onClick={() => setActiveSection(it.key as SectionKey)}
                  disabled={!selectedChildId}
                >
                  {it.label}
                </Button>
              ))}
            </div>

            {activeSection !== 'profesori' && activeSection !== 'secretariat' && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-sm text-muted-foreground">Filtre:</div>
                <select
                  className="min-w-[220px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={loadingConfigs || gradebookConfigs.length === 0}
                  value={activeGradebookId ?? ''}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setActiveGradebookId(Number.isFinite(v) ? v : null);
                    setActivePeriodIndex(0);
                  }}
                >
                  {gradebookConfigs.length === 0 ? (
                    <option value="">Niciun catalog</option>
                  ) : (
                    gradebookConfigs.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.school_year} • {c.structure}
                      </option>
                    ))
                  )}
                </select>

                <div className="flex gap-2 flex-wrap">
                  {periodTabs.map((label, idx) => (
                    <Button
                      key={label}
                      variant={activePeriodIndex === idx ? 'default' : 'outline'}
                      size="xs"
                      onClick={() => setActivePeriodIndex(idx)}
                      disabled={!activeGradebookId}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {selectedChild && !selectedChild.classroom_id && activeSection !== 'profesori' && activeSection !== 'secretariat' && (
              <div className="text-sm text-muted-foreground">
                Copilul nu este inscris intr-o clasa (nu pot incarca orar/teme/note/absente).
              </div>
            )}

            {activeSection === 'orar' && (
              <div>
                {loadingOrar ? (
                  <div className="text-sm text-muted-foreground">Se incarca orarul...</div>
                ) : orarError ? (
                  <div className="text-sm text-error">{orarError}</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {days.map((day, idx) => {
                      const entries = orar
                        .filter((e) => e.day_of_week === idx)
                        .slice()
                        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
                      return (
                        <div key={day} className="border border-border rounded-lg p-3">
                          <div className="font-semibold text-foreground mb-2">{day}</div>
                          {entries.length === 0 ? (
                            <div className="text-sm text-muted-foreground">—</div>
                          ) : (
                            <div className="space-y-2">
                              {entries.map((e) => (
                                <div key={e.id} className="bg-muted/40 rounded p-2">
                                  <div className="text-sm font-medium text-foreground">
                                    {e.start_time}–{e.end_time} • {e.subject}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {e.teacher_name ? `Prof. ${e.teacher_name}` : '—'}
                                    {e.room ? ` • Sala ${e.room}` : ''}
                                    {e.lesson_type ? ` • ${e.lesson_type}` : ''}
                                  </div>
                                  {e.comment && <div className="text-xs text-muted-foreground mt-1">{e.comment}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {(activeSection === 'teme_profesor' || activeSection === 'teme_elev') && (
              <div>
                {loadingTeme ? (
                  <div className="text-sm text-muted-foreground">Se incarca temele...</div>
                ) : temeError ? (
                  <div className="text-sm text-error">{temeError}</div>
                ) : (activeSection === 'teme_profesor' ? temeProfesor : temeElev).length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nu exista teme pentru filtrul ales.</div>
                ) : (
                  <div className="space-y-3">
                    {(activeSection === 'teme_profesor' ? temeProfesor : temeElev).map((t) => (
                      <div key={t.id} className="border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <div className="font-semibold text-foreground">{t.title}</div>
                            <div className="text-xs text-muted-foreground">
                              {t.subject ? `${t.subject} • ` : ''}
                              {t.teacher_name ? `Prof. ${t.teacher_name} • ` : ''}
                              {t.created_at ? new Date(t.created_at).toLocaleString('ro-RO') : ''}
                            </div>
                            {t.description && (
                              <div className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{t.description}</div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div
                              className={`text-xs font-semibold px-2 py-1 rounded ${
                                t.submission ? 'bg-success/20 text-success-foreground' : 'bg-warning/20 text-warning-foreground'
                              }`}
                            >
                              {t.submission ? 'Trimis' : 'Netimis'}
                            </div>

                            {t.has_quiz ? (
                              <div className="flex flex-col items-end gap-1">
                                {t.quiz_submission?.submitted_at ? (
                                  <div className="text-xs text-muted-foreground">
                                    Quiz: {t.quiz_submission?.score ?? '—'} / {t.quiz_submission?.total ?? '—'}
                                  </div>
                                ) : (
                                  <div className="text-xs text-muted-foreground">Quiz disponibil</div>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="HelpCircle"
                                  onClick={() => setQuizHomeworkId(t.id)}
                                >
                                  Quiz
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="text-sm font-medium text-foreground mb-1">Fisiere profesor</div>
                          {t.files?.length ? (
                            <ul className="list-disc pl-5 text-sm">
                              {t.files.map((f, i) => (
                                <li key={i}>
                                  <button className="text-primary hover:underline" onClick={() => void doDownloadHomeworkFile(Number(f.id))}>
                                    {f.original_name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-sm text-muted-foreground">—</div>
                          )}
                        </div>

                        {t.submission && (
                          <div className="mt-4 bg-muted/30 border border-border rounded p-3">
                            <div className="text-sm font-medium text-foreground mb-1">Rezolvare elev</div>
                            <div className="text-xs text-muted-foreground">
                              Ultima actualizare: {new Date(t.submission.updated_at).toLocaleString('ro-RO')}
                            </div>
                            {t.submission.comment && (
                              <div className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{t.submission.comment}</div>
                            )}
                            <div className="mt-2">
                              {t.submission.files?.length ? (
                                <ul className="list-disc pl-5 text-sm">
                                  {t.submission.files.map((f, i) => (
                                    <li key={i}>
                                      <button className="text-primary hover:underline" onClick={() => void doDownloadSubmissionFile(Number(f.id))}>
                                        {f.original_name}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-sm text-muted-foreground">—</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'note' && (
              <div>
                {loadingNote ? (
                  <div className="text-sm text-muted-foreground">Se incarca notele...</div>
                ) : noteError ? (
                  <div className="text-sm text-error">{noteError}</div>
                ) : note.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nu exista note pentru filtrul ales.</div>
                ) : (
                  <div className="space-y-3">
                    {groupedNote.map((grp) => (
                      <details key={grp.label} className="rounded-lg border border-border bg-background" open>
                        <summary className="cursor-pointer select-none px-4 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold text-foreground truncate">{grp.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {grp.avg != null ? `Media: ${grp.avg}` : 'Media: —'}{' '}
                                <span className="opacity-80">({grp.avgCount} note)</span> • {grp.totalCount} înregistrări
                              </div>
                            </div>
                          </div>
                        </summary>
                        <div className="px-4 pb-4 overflow-auto">
                          <table className="min-w-[760px] w-full text-sm">
                            <thead>
                              <tr className="text-left border-b border-border">
                                <th className="py-2 pr-3">Nota</th>
                                <th className="py-2 pr-3">Tip</th>
                                <th className="py-2 pr-3">Profesor</th>
                                <th className="py-2 pr-3">Data</th>
                                <th className="py-2 pr-3">Comentariu</th>
                              </tr>
                            </thead>
                            <tbody>
                              {grp.grades.map((n) => {
                                const val = n.value != null ? String(n.value) : n.qualifier || '—';
                                return (
                                  <tr key={n.id} className="border-b border-border">
                                    <td className="py-2 pr-3 font-semibold">{val}</td>
                                    <td className="py-2 pr-3">{gradeTypeLabel(n)}</td>
                                    <td className="py-2 pr-3">{n.teacher_name || '—'}</td>
                                    <td className="py-2 pr-3">{n.created_at ? new Date(n.created_at).toLocaleDateString('ro-RO') : '—'}</td>
                                    <td className="py-2 pr-3">{n.comment || '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'absente' && (
              <div>
                {loadingAbsente ? (
                  <div className="text-sm text-muted-foreground">Se incarca absentele...</div>
                ) : absenteError ? (
                  <div className="text-sm text-error">{absenteError}</div>
                ) : absente.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nu exista absente pentru filtrul ales.</div>
                ) : (
                  <div className="overflow-auto">
                    <table className="min-w-[780px] w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-border">
                          <th className="py-2 pr-3">Data</th>
                          <th className="py-2 pr-3">Materie</th>
                          <th className="py-2 pr-3">Motivata</th>
                          <th className="py-2 pr-3">Motivare</th>
                        </tr>
                      </thead>
                      <tbody>
                        {absente
                          .slice()
                          .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                          .map((a) => (
                            <tr key={a.id} className="border-b border-border">
                              <td className="py-2 pr-3">{a.date ? new Date(a.date).toLocaleDateString('ro-RO') : '—'}</td>
                              <td className="py-2 pr-3">{a.subject_name || (a.subject_id != null ? `#${a.subject_id}` : '—')}</td>
                              <td className="py-2 pr-3">
                                <span
                                  className={`px-2 py-1 rounded text-xs font-semibold ${
                                    String(a.justified).toLowerCase() === 'da'
                                      ? 'bg-success/20 text-success-foreground'
                                      : 'bg-error/20 text-error-foreground'
                                  }`}
                                >
                                  {String(a.justified).toLowerCase() === 'da' ? 'DA' : 'NU'}
                                </span>
                              </td>
                              <td className="py-2 pr-3">{a.justification_reason || '—'}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'secretariat' && (
              <div>
                {!selectedChildId ? (
                  <div className="text-sm text-muted-foreground">Selecteaza un copil.</div>
                ) : (
                  <ParentSecretariatPanel childId={Number(selectedChildId)} childName={selectedChild?.full_name || null} />
                )}
              </div>
            )}

            {activeSection === 'profesori' && (
              <div>
                {!selectedChild?.classroom_id ? (
                  <div className="text-sm text-muted-foreground">
                    Copilul selectat nu este asignat unei clase.
                  </div>
                ) : (
                  <ProfesoriPanel classroomId={Number(selectedChild.classroom_id)} />
                )}
              </div>
            )}
          </div>

          <ParentNotificationsCard
            notifications={notifications}
            onReload={async () => {
              await loadNotifications();
              await refreshUnreadNotifications();
            }}
            onMarkAllRead={markAllNotificationsRead}
            onOpenAll={() => navigate('/notifications')}
            onOpen={openNotification}
          />
        </div>
      </main>

      {activateOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Activeaza cont elev</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Elevul se va autentifica cu <strong>username + parola</strong>. Pentru elevii sub 16 ani, datele sunt gestionate de parinte.
            </p>

            {activateError && <div className="mb-3 text-sm text-error">{activateError}</div>}

            <div className="space-y-3">
              <input
                value={activateUsername}
                onChange={(e) => setActivateUsername(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Username (ex: ion.popescu)"
                autoComplete="username"
              />
              <input
                type="password"
                value={activatePassword}
                onChange={(e) => setActivatePassword(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Parola"
                autoComplete="new-password"
              />
              <input
                type="password"
                value={activateConfirm}
                onChange={(e) => setActivateConfirm(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Confirma parola"
                autoComplete="new-password"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActivateOpen(false)} disabled={activateLoading}>
                Inchide
              </Button>
              <Button variant="default" onClick={activateChildAccount} loading={activateLoading}>
                Salveaza
              </Button>
            </div>
          </div>
        </div>
      )}

      {avatarOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Imagine profil copil</h2>
            <p className="text-sm text-muted-foreground mb-4">Pentru elevii sub 16 ani, poza este gestionata de parinte.</p>

            {avatarError && <div className="mb-3 text-sm text-error">{avatarError}</div>}

            <label className="block text-sm font-medium text-foreground">
              Alege imagine
              <input
                type="file"
                accept="image/*"
                className="mt-2 block w-full text-sm text-muted-foreground"
                disabled={avatarLoading}
                onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
              />
            </label>

            <div className="mt-4 flex justify-between gap-2">
              <Button variant="outline" onClick={deleteChildAvatar} disabled={avatarLoading || !selectedChild?.avatar_url}>
                Sterge
              </Button>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAvatarOpen(false)} disabled={avatarLoading}>
                  Inchide
                </Button>
                <Button variant="default" onClick={uploadChildAvatar} loading={avatarLoading}>
                  Salveaza
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPwdModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Schimba parola initiala</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Pentru securitate, te rugam sa iti setezi o parola noua.
            </p>

            {pwdError && <div className="mb-3 text-sm text-error">{pwdError}</div>}

            <div className="space-y-3">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Parola noua"
                autoComplete="new-password"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Confirma parola"
                autoComplete="new-password"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  signOut();
                  navigate('/authentication-portal');
                }}
              >
                Delogare
              </Button>
              <Button variant="default" onClick={handleChangePassword} loading={pwdLoading}>
                Salveaza
              </Button>
            </div>
          </div>
        </div>
      )}

      <HomeworkQuizModal
        open={quizHomeworkId != null}
        homeworkId={quizHomeworkId ?? 0}
        onClose={() => setQuizHomeworkId(null)}
        readOnly
        resultSummary={quizResultSummary}
      />

      <SubscriptionRequiredModal open={upgradeOpen} feature={upgradeFeature} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default ParentDashboard;
